import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Contact, X, Download, Plus, RefreshCw } from "lucide-react";
import { WhatsAppLink } from "@/components/WhatsAppLink";
import { SearchableSelect } from "@/components/SearchableSelect";
import { toast } from "sonner";
import { ExportDialog, DATE_FIELDS_BASE, LEAD_EXPORT_FIELDS } from "@/components/ExportDialog";
import { useExports } from "@/context/ExportsContext";

const EMPTY = { nome: "", cognome: "", cf: "", cellulare: "", email: "", provincia: "", citta: "all", professione: "all", assigned_to: "all", esito: "all", campaign_id: "all", created_from: "", created_to: "", updated_from: "", updated_to: "" };
const NL_EMPTY = { nome: "", cognome: "", codice_fiscale: "", cellulare: "", email: "", provincia: "", citta: "", professione: "", note: "" };

export default function RubricaLead() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [facets, setFacets] = useState({ citta: [], professioni: [] });
  const [exportOpen, setExportOpen] = useState(false);
  const [internalUsers, setInternalUsers] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [newOpen, setNewOpen] = useState(false);
  const [nl, setNl] = useState(NL_EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState([]);
  const [targetCamp, setTargetCamp] = useState("");
  const [bulkOp, setBulkOp] = useState("none");
  const allSelected = leads.length > 0 && leads.every((l) => sel.includes(l.id));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const toggleSel = (id) => setSel((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const toggleAll = () => setSel(allSelected ? [] : leads.map((l) => l.id));
  const selectAllResults = async () => {
    try {
      const { data } = await api.get("/leads/ids", { params: buildParams() });
      setSel(data.ids); toast.success(`${data.ids.length} lead selezionati`);
    } catch { toast.error("Errore selezione"); }
  };
  const assignToCampaign = async () => {
    if (!targetCamp) { toast.error("Scegli una campagna"); return; }
    if (sel.length === 0) { toast.error("Seleziona almeno un lead"); return; }
    try {
      const { data } = await api.post(`/campaigns/${targetCamp}/leads/from-leads`, { lead_ids: sel });
      toast.success(`${data.created} lead assegnati · ${data.skipped_duplicate} duplicati`);
      setSel([]); setTargetCamp(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };
  const bulkUpdate = async () => {
    if (bulkOp === "none") { toast.error("Scegli un operatore"); return; }
    try {
      const body = { lead_ids: sel, assigned_to: bulkOp === "unassign" ? "" : bulkOp };
      const { data } = await api.put("/leads/bulk-update", body);
      toast.success(`${data.updated} lead aggiornati`);
      setSel([]); setBulkOp("none"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Eliminare definitivamente ${sel.length} lead selezionati? L'operazione non è reversibile.`)) return;
    try {
      const { data } = await api.post("/leads/bulk-delete", { ids: sel });
      toast.success(`${data.deleted} lead eliminati`);
      setSel([]); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };

  const buildParams = useCallback(() => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    return params;
  }, [f]);

  const load = useCallback(() => {
    setLoading(true);
    const params = buildParams();
    api.get("/leads", { params: { ...params, page, page_size: pageSize } }).then((r) => setLeads(r.data)).finally(() => setLoading(false));
    api.get("/leads/count", { params }).then((r) => setTotal(r.data.total)).catch(() => {});
  }, [buildParams, page, pageSize]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [f, pageSize]);
  useEffect(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
    api.get("/leads/facets").then((r) => setFacets(r.data)).catch(() => {});
    api.get("/users/internal").then((r) => setInternalUsers(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const reset = () => setF(EMPTY);
  const hasFilters = Object.entries(f).some(([, v]) => v && v !== "all");

  const createLead = async () => {
    if (!nl.nome.trim() && !nl.cognome.trim() && !nl.codice_fiscale.trim()) { toast.error("Inserisci almeno Nome, Cognome o Codice Fiscale"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/leads", { ...nl, campaign_id: "" });
      toast.success(`Lead creato (${data.lead_code})`);
      setNewOpen(false); setNl(NL_EMPTY); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore creazione lead"); }
    finally { setSaving(false); }
  };

  const { startExport } = useExports();
  const exportExcel = (params = {}) => startExport("leads_export", { ...buildParams(), ...params }, "Lead (Rubrica)");

  return (
    <div className="space-y-6" data-testid="rubrica-lead-page">
      <button onClick={() => navigate("/marketing")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-marketing"><ArrowLeft className="h-4 w-4" /> Torna al Marketing</button>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Contact className="h-6 w-6 text-primary" /> Rubrica Lead</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} lead{hasFilters ? " (filtrati)" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="rl-new-lead-btn" onClick={() => setNewOpen(true)}><Plus className="h-4 w-4 mr-2" />Nuovo Lead</Button>
          {isAdmin && <Button variant="outline" data-testid="rl-export-btn" onClick={() => setExportOpen(true)}><Download className="h-4 w-4 mr-2" />Esporta risultati</Button>}
          {isAdmin && <Button variant="ghost" size="icon" title="Re-indicizza ricerca (ripara ricerche che non trovano lead)" data-testid="rl-reindex-btn" onClick={async () => { try { await api.post("/leads/reindex-search"); toast.success("Re-indicizzazione avviata: attendi qualche minuto, poi riprova la ricerca"); } catch { toast.error("Errore re-indicizzazione"); } }}><RefreshCw className="h-4 w-4" /></Button>}
        </div>
      </div>
      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} title="Esporta Lead (Rubrica)" fields={LEAD_EXPORT_FIELDS} dateFields={DATE_FIELDS_BASE} onConfirm={exportExcel} />

      <Dialog open={newOpen} onOpenChange={(o) => { setNewOpen(o); if (!o) setNl(NL_EMPTY); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="rl-new-lead-dialog">
          <DialogHeader><DialogTitle>Nuovo Lead</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="space-y-1.5"><Label>Nome</Label><Input data-testid="nl-nome" value={nl.nome} onChange={(e) => setNl({ ...nl, nome: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Cognome</Label><Input data-testid="nl-cognome" value={nl.cognome} onChange={(e) => setNl({ ...nl, cognome: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Codice Fiscale</Label><Input data-testid="nl-cf" value={nl.codice_fiscale} onChange={(e) => setNl({ ...nl, codice_fiscale: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Cellulare</Label><Input data-testid="nl-cellulare" value={nl.cellulare} onChange={(e) => setNl({ ...nl, cellulare: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="nl-email" value={nl.email} onChange={(e) => setNl({ ...nl, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Provincia</Label><Input data-testid="nl-provincia" value={nl.provincia} onChange={(e) => setNl({ ...nl, provincia: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Città</Label><Input data-testid="nl-citta" value={nl.citta} onChange={(e) => setNl({ ...nl, citta: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Professione</Label><Input data-testid="nl-professione" value={nl.professione} onChange={(e) => setNl({ ...nl, professione: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Note</Label><Textarea data-testid="nl-note" value={nl.note} onChange={(e) => setNl({ ...nl, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Annulla</Button>
            <Button data-testid="nl-save" onClick={createLead} disabled={saving}>{saving ? "Salvataggio…" : "Crea lead"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><label className="text-xs text-muted-foreground">Nome</label><Input data-testid="rl-filter-nome" value={f.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Nome" /></div>
          <div><label className="text-xs text-muted-foreground">Cognome</label><Input data-testid="rl-filter-cognome" value={f.cognome} onChange={(e) => set("cognome", e.target.value)} placeholder="Cognome" /></div>
          <div><label className="text-xs text-muted-foreground">Codice Fiscale</label><Input data-testid="rl-filter-cf" value={f.cf} onChange={(e) => set("cf", e.target.value)} placeholder="CF" /></div>
          <div><label className="text-xs text-muted-foreground">Cellulare</label><Input data-testid="rl-filter-cellulare" value={f.cellulare} onChange={(e) => set("cellulare", e.target.value)} placeholder="Telefono" /></div>
          <div><label className="text-xs text-muted-foreground">Email</label><Input data-testid="rl-filter-email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" /></div>
          <div><label className="text-xs text-muted-foreground">Provincia</label><Input data-testid="rl-filter-provincia" value={f.provincia} onChange={(e) => set("provincia", e.target.value)} placeholder="Provincia" /></div>
          <div><label className="text-xs text-muted-foreground">Città</label>
            <Select value={f.citta} onValueChange={(v) => set("citta", v)}>
              <SelectTrigger data-testid="rl-filter-citta"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte le città</SelectItem>{facets.citta.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs text-muted-foreground">Professione</label>
            <SearchableSelect value={f.professione} onValueChange={(v) => set("professione", v)} testid="rl-filter-professione"
              placeholder="Tutte le professioni" searchPlaceholder="Cerca professione…"
              options={[{ value: "all", label: "Tutte le professioni" }, ...facets.professioni.map((p) => ({ value: p, label: p }))]} />
          </div>
          <div><label className="text-xs text-muted-foreground">Assegnato a</label>
            <SearchableSelect value={f.assigned_to} onValueChange={(v) => set("assigned_to", v)} testid="rl-filter-assigned"
              placeholder="Tutti" searchPlaceholder="Cerca operatore…"
              options={[{ value: "all", label: "Tutti" }, ...internalUsers.map((u) => ({ value: u.id, label: u.name }))]} />
          </div>
          <div><label className="text-xs text-muted-foreground">Campagna</label>
            <Select value={f.campaign_id} onValueChange={(v) => set("campaign_id", v)}>
              <SelectTrigger data-testid="rl-filter-campaign"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte le campagne</SelectItem>{campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs text-muted-foreground">Caricato dal</label><Input type="date" data-testid="rl-filter-created-from" value={f.created_from} onChange={(e) => set("created_from", e.target.value)} /></div>
          <div><label className="text-xs text-muted-foreground">Caricato al</label><Input type="date" data-testid="rl-filter-created-to" value={f.created_to} onChange={(e) => set("created_to", e.target.value)} /></div>
          <div><label className="text-xs text-muted-foreground">Modificato dal</label><Input type="date" data-testid="rl-filter-updated-from" value={f.updated_from} onChange={(e) => set("updated_from", e.target.value)} /></div>
          <div><label className="text-xs text-muted-foreground">Modificato al</label><Input type="date" data-testid="rl-filter-updated-to" value={f.updated_to} onChange={(e) => set("updated_to", e.target.value)} /></div>
        </div>
        {hasFilters && <Button variant="ghost" size="sm" className="mt-3" data-testid="rl-reset" onClick={reset}><X className="h-3.5 w-3.5 mr-1" />Azzera filtri</Button>}
      </Card>

      {sel.length > 0 && (
        <Card className="p-3 flex flex-wrap items-center gap-3" data-testid="rl-bulk-bar">
          <span className="text-sm font-medium">{sel.length} lead selezionati</span>
          {allSelected && total > leads.length && sel.length < total && (
            <Button variant="link" size="sm" className="h-auto p-0 text-primary" data-testid="rl-select-all-results" onClick={selectAllResults}>Seleziona tutti i {total} risultati</Button>
          )}
          <div className="flex flex-wrap items-center gap-2 ml-auto">
            <SearchableSelect value={bulkOp} onValueChange={setBulkOp} className="w-40" testid="rl-bulk-operator"
              placeholder="Assegna operatore…" searchPlaceholder="Cerca operatore…"
              options={[{ value: "none", label: "Assegna operatore…" }, { value: "unassign", label: "Nessuno" }, ...internalUsers.map((u) => ({ value: u.id, label: u.name }))]} />
            <Button variant="secondary" data-testid="rl-bulk-apply" onClick={bulkUpdate}>Applica</Button>
            <span className="w-px h-6 bg-border mx-1" />
            <Select value={targetCamp} onValueChange={setTargetCamp}>
              <SelectTrigger className="w-52" data-testid="rl-assign-campaign"><SelectValue placeholder="Assegna a campagna…" /></SelectTrigger>
              <SelectContent>{campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="rl-assign-btn" onClick={assignToCampaign}>Assegna</Button>
            {isAdmin && <><span className="w-px h-6 bg-border mx-1" /><Button variant="destructive" data-testid="rl-bulk-delete" onClick={bulkDelete}>Elimina</Button></>}
            <Button variant="ghost" data-testid="rl-assign-cancel" onClick={() => setSel([])}>Annulla</Button>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="rubrica-lead-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-3 w-8"><input type="checkbox" className="accent-primary" checked={allSelected} onChange={toggleAll} data-testid="rl-select-all" /></th>
                <th className="px-4 py-3 font-semibold">Nominativo</th>
                <th className="px-4 py-3 font-semibold">Codice Fiscale</th>
                <th className="px-4 py-3 font-semibold">Cellulare</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Provincia</th>
                <th className="px-4 py-3 font-semibold">Campagna</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} data-testid={`rl-row-${l.id}`} onClick={() => navigate(l.campaign_id ? `/marketing/${l.campaign_id}/lead/${l.id}` : `/marketing/rubrica/lead/${l.id}`)}
                    className="border-b border-border last:border-0 hover:bg-accent/40 cursor-pointer">
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" className="accent-primary" checked={sel.includes(l.id)} onChange={() => toggleSel(l.id)} data-testid={`rl-check-${l.id}`} /></td>
                  <td className="px-4 py-3 font-medium">{`${l.nome || ""} ${l.cognome || ""}`.trim() || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{l.codice_fiscale || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground"><span className="inline-flex items-center gap-1.5">{l.cellulare || l.telefono || "—"}{(l.cellulare || l.telefono) && <WhatsAppLink number={l.cellulare || l.telefono} />}</span></td>
                  <td className="px-4 py-3 text-muted-foreground truncate max-w-[180px]">{l.email || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{l.provincia || l.residenza || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{l.campaign_name || "—"}</td>
                </tr>
              ))}
              {leads.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">{loading ? "Caricamento…" : "Nessun lead trovato"}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="md:hidden divide-y divide-border" data-testid="rubrica-lead-mobile-list">
          {leads.map((l) => (
            <div key={l.id} data-testid={`rl-card-${l.id}`} onClick={() => navigate(l.campaign_id ? `/marketing/${l.campaign_id}/lead/${l.id}` : `/marketing/rubrica/lead/${l.id}`)} className="p-4 hover:bg-accent/40 cursor-pointer">
              <div className="flex items-start gap-2.5">
                <input type="checkbox" className="accent-primary mt-1" checked={sel.includes(l.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggleSel(l.id)} data-testid={`rl-check-m-${l.id}`} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium break-words">{`${l.nome || ""} ${l.cognome || ""}`.trim() || "—"}</div>
                  <div className="mt-1 grid grid-cols-1 gap-y-1 text-xs text-muted-foreground">
                    {l.codice_fiscale && <div className="font-mono">{l.codice_fiscale}</div>}
                    <div className="inline-flex items-center gap-1.5">{l.cellulare || l.telefono || "—"}{(l.cellulare || l.telefono) && <WhatsAppLink number={l.cellulare || l.telefono} />}</div>
                    {l.email && <div className="break-words">{l.email}</div>}
                    {(l.provincia || l.residenza) && <div>{l.provincia || l.residenza}</div>}
                    {l.campaign_name && <div><span className="font-medium text-foreground">Campagna:</span> {l.campaign_name}</div>}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {leads.length === 0 && <div className="px-4 py-12 text-center text-muted-foreground">{loading ? "Caricamento…" : "Nessun lead trovato"}</div>}
        </div>
        {total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-border" data-testid="rl-pagination">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Righe per pagina</span>
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                <SelectTrigger className="h-8 w-[84px]" data-testid="rl-pagination-size"><SelectValue /></SelectTrigger>
                <SelectContent>{[25, 50, 100, 200].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span data-testid="rl-pagination-info">{total === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} di {total}</span>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="rl-pagination-prev"><ArrowLeft className="h-4 w-4" /></Button>
                <span className="px-1 tabular-nums">{page}/{pageCount}</span>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))} data-testid="rl-pagination-next"><ArrowLeft className="h-4 w-4 rotate-180" /></Button>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
